import React, { useMemo } from "react";
import { FileText, ExternalLink } from "lucide-react";
import Modal from "../../../common/Modal";
import { useAuth } from "../../../../store/hooks/useAuth";
import type { MyExit } from "../../Utils/types";
import {
  extractFilenameFromUrl,
  pickAttachmentLinkFromExit,
  pickAttachmentNameFromExit,
} from "../../Utils/attachmentUtils";
import {
  asExitRecord,
  exitListStatusLabel,
  functionalManagerDisplay,
  lastWorkingDisplay,
  reasonDisplay,
  submittedOnDisplay,
} from "../../Utils/exitRecordDisplay";
import { formatExitDateDisplay } from "../../Utils/exitDateUtils";
import NoticePeriodCard from "./NoticePeriodCard";

export interface MyExitViewModalProps {
  exit: MyExit | null;
  onClose: () => void;
}

function DetailRow({
  label,
  value,
  truncate,
}: {
  label: string;
  value: React.ReactNode;
  truncate?: boolean;
}) {
  const isEmpty =
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "");
  const displayValue = isEmpty ? "—" : value;

  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[11px] font-bold text-slate-400 tracking-wider">
        {label}
      </span>
      <div
        className={`text-[14px] font-medium text-slate-800 leading-snug ${truncate ? "truncate" : "whitespace-pre-wrap break-words"
          }`}
        title={truncate && typeof displayValue === "string" ? displayValue : undefined}
      >
        {displayValue}
      </div>
    </div>
  );
}

const MyExitViewModal: React.FC<MyExitViewModalProps> = ({ exit, onClose }) => {
  const { user } = useAuth();
  const row = exit ? asExitRecord(exit) : null;
  const attachmentHref =
    exit && row ? pickAttachmentLinkFromExit(row) : undefined;
  const rawAttachmentName =
    exit && row ? pickAttachmentNameFromExit(row) : undefined;
  const attachmentName =
    rawAttachmentName ||
    (attachmentHref ? extractFilenameFromUrl(attachmentHref) : "Document");
  const emp = exit?.employee;

  /** my-exit often omits FM on `employee`; session user (same as profile) usually has it. */
  const functionalManagerValue = useMemo(() => {
    if (!exit) return "—";
    const fromPayload = functionalManagerDisplay(exit);
    if (fromPayload !== "—") return fromPayload;
    const fromSession = user?.functionalManagerName?.trim();
    return fromSession || "—";
  }, [exit, user?.functionalManagerName]);

  return (
    <Modal
      isOpen={!!exit}
      onClose={onClose}
      title="Resignation details"
      size="4xl"
    >
      {exit && (
        <div className="w-full pb-2 pt-1">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
            <div className="space-y-5">
              <DetailRow label="Employee Name" value={emp?.fullName} />
              <DetailRow label="Employee Id" value={emp?.employeeCode} />
              <DetailRow label="Email" value={emp?.workEmail} />
              <DetailRow label="Department" value={emp?.department} />
              <DetailRow label="Designation" value={emp?.designation} />
              <DetailRow
                label="Reporting manager"
                value={
                  emp?.reportingManagerName &&
                    emp.reportingManagerName.trim()
                    ? emp.reportingManagerName
                    : "—"
                }
              />
              <DetailRow
                label="Functional manager"
                value={functionalManagerValue}
              />

            </div>

            <div className="space-y-5">
              <DetailRow
                label="Approval Status"
                value={exitListStatusLabel(exit)}
              />
              {/* <DetailRow
                label="Resignation date"
                value={
                  exit.resignationDate
                    ? formatExitDateDisplay(exit.resignationDate)
                    : "—"
                }
              /> */}
              <DetailRow
                label="Requested Last Working Date"
                value={lastWorkingDisplay(exit)}
              />

              {/* {typeof exit.noticePeriodDays === "number" ? (
                <DetailRow
                  label="Notice period (days)"
                  value={String(exit.noticePeriodDays)}
                />
              ) : null} */}
              <DetailRow label="Reason" value={reasonDisplay(exit)} />
              {exit.comments ? (
                <DetailRow label="Comments" value={exit.comments} truncate />
              ) : null}
              {exit.workflow?.remarks?.map((r, i) => (
                <DetailRow
                  key={i}
                  label={`Remark by ${r.actorName}`}
                  value={r.remark}
                  truncate
                />
              ))}
              <DetailRow
                label="Submitted on"
                value={submittedOnDisplay(exit)}
              />
              <DetailRow
                label="Document"
                value={
                  attachmentHref ? (
                    <a
                      href={attachmentHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center gap-2.5 max-w-xs px-3 py-2 mt-1 rounded-lg border border-slate-200 bg-slate-50/70 hover:bg-primary-50/50 hover:border-primary-300 transition-all text-slate-700 hover:text-primary-700 shadow-sm"
                      title={attachmentName}
                    >
                      <FileText className="w-4 h-4 text-primary-600 shrink-0" />
                      <span className="text-sm font-medium truncate flex-1">
                        {attachmentName}
                      </span>
                      <ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover:text-primary-600 transition-colors shrink-0" />
                    </a>
                  ) : (
                    <span className="text-slate-400 text-sm">No document uploaded</span>
                  )
                }
              />
            </div>
          </div>

          {exit.noticePeriodTracking &&
            typeof exit.noticePeriodTracking === "object" ? (
            <div className="mt-6">
              <NoticePeriodCard tracking={exit.noticePeriodTracking} />
            </div>
          ) : null}

          {/* {exit.workflow ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-3">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Workflow
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
                <DetailRow
                  label="Label"
                  value={exit.workflow.workflowStatusLabel ?? "—"}
                />
                <DetailRow
                  label="Workflow status"
                  value={exit.workflow.workflowStatus ?? "—"}
                />
                <DetailRow
                  label="Approval instance"
                  value={exit.workflow.approvalInstanceId ?? "—"}
                />
                <DetailRow
                  label="Flags"
                  value={
                    [
                      exit.workflow.isCompleted ? "Completed" : null,
                      exit.workflow.isRejected ? "Rejected" : null,
                      exit.workflow.isCancelled ? "Cancelled" : null,
                    ]
                      .filter(Boolean)
                      .join(", ") || "—"
                  }
                />
              </div>
            </div>
          ) : null} */}
        </div>
      )}
    </Modal>
  );
};

export default MyExitViewModal;
